import { OpenImgContextProvider } from 'openimg/react'
import {
	data,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useMatches,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { DashboardLayout } from './components/dashboard-layout.tsx'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { I18nProviderOptimized } from './components/i18n-provider-optimized.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { GlobalStateProvider } from './contexts/global-state.tsx'
import { DataFetchingProvider } from './contexts/query-client.tsx'
import { loadRouteTranslations } from './utils/i18n.server.optimized.ts'
import {
	useOptionalTheme,
	useTheme,
} from './routes/resources+/theme-switch.tsx'
import offertyStyleSheetUrl from './styles/offerty.css?url'
import recoletaStyleSheetUrl from './styles/recoleta.css?url'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser } from './utils/user.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		// Preload font stylesheets
		{ rel: 'preload', href: offertyStyleSheetUrl, as: 'style' },
		{ rel: 'preload', href: recoletaStyleSheetUrl, as: 'style' },
		// Preload common namespace for the default locale
		{
			rel: 'preload',
			href: '/locales/nl/common.json',
			as: 'fetch',
			crossOrigin: 'use-credentials',
		} as const,

		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
		{ rel: 'stylesheet', href: offertyStyleSheetUrl },
		{ rel: 'stylesheet', href: recoletaStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{ title: data ? 'Epic Notes' : 'Error | Epic Notes' },
		{ name: 'description', content: `Your own captain's log` },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	// Get pathname from request URL
	const url = new URL(request.url)
	const pathname = url.pathname

	// Load optimized translations for the route
	const { locale, resources } = await time(
		() => loadRouteTranslations(request, pathname),
		{
			timings,
			type: 'loadTranslations',
			desc: 'load i18n translations',
		}
	)

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { objectKey: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
			locale,
			i18nResources: resources,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
	locale = 'en',
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
	locale?: string
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang={locale} className={`${theme}`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	const nonce = useNonce()
	const theme = useOptionalTheme()
	// Note: Layout cannot use useLoaderData in React Router v7
	// Data must be passed from the route component
	return (
		<Document nonce={nonce} theme={theme}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const theme = useTheme()
	const nonce = useNonce()
	const matches = useMatches()
	useToast(data.toast)

	// Document-engine surfaces (/print/:documentId) must render with zero
	// chrome in every auth state — the PDF service prints exactly this markup
	// (MASTER_PLAN §4.1). Keyed off the matched route's handle rather than the
	// pathname because route matching is case-insensitive (/Print/... would
	// dodge a string prefix check).
	const isBareDocumentSurface = matches.some(
		(match) =>
			(match.handle as { bareDocument?: boolean } | undefined)?.bareDocument,
	)

	return (
		<Document nonce={nonce} theme={theme} env={data.ENV} locale={data.locale}>
			<OpenImgContextProvider
				optimizerEndpoint="/resources/images"
				getSrc={getImgSrc}
			>
				{isBareDocumentSurface ? (
					<Outlet />
				) : user ? (
					<>
						<DashboardLayout />
						<EpicToaster closeButton position="top-center" theme={theme} />
						<EpicProgress />
					</>
				) : (
					<>
						<Outlet />
						<EpicToaster closeButton position="top-center" theme={theme} />
						<EpicProgress />
					</>
				)}
			</OpenImgContextProvider>
		</Document>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	const theme = useOptionalTheme() ?? 'system'

	return (
		<HoneypotProvider {...data.honeyProps}>
			<I18nProviderOptimized lng={data.locale} resources={data.i18nResources}>
				<DataFetchingProvider>
					<GlobalStateProvider
						initialState={{
							user: data.user,
							theme,
							locale: data.locale,
						}}
					>
						<App />
					</GlobalStateProvider>
				</DataFetchingProvider>
			</I18nProviderOptimized>
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
